// ============================================================
// TPO Terminal — Overlays: Rechtsklick-Kontextmenü (Block D),
// Alarm-Dialog (Block D, nutzt bestehendes price_alerts-System),
// Snapshot-Dialog (Block H, an Trade/Journal hängen).
// Reine Präsentation; State lebt in Terminal.tsx.
// ============================================================
import React, { useEffect, useRef, useState } from 'react';
import { tradesApi, Trade } from '../../services/api';
import { fmtPrice } from './types';

const API = '/api';

// ---- gemeinsame Styles ----
const overlayBg: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 60, background: '#0006', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const panel: React.CSSProperties = { background: '#0c1120', border: '1px solid #1e2539', borderRadius: 12, padding: 18, width: 420, maxWidth: '92vw', boxShadow: '0 20px 60px rgba(0,0,0,.6)' };
const label: React.CSSProperties = { color: '#94a3b8', fontSize: 12, marginBottom: 4, display: 'block' };
const input: React.CSSProperties = { background: '#0a0e1a', color: '#fff', border: '1px solid #1e2539', borderRadius: 6, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' };
const btnPrimary: React.CSSProperties = { background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 7, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const btnGhost: React.CSSProperties = { background: 'transparent', color: '#8b93a7', border: '1px solid #1e2539', borderRadius: 7, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' };

// ============================================================
// Kontextmenü (Block D)
// ============================================================
export interface MenuItem { label: string; onClick: () => void; danger?: boolean; }

export const ContextMenu: React.FC<{ x: number; y: number; items: MenuItem[]; onClose: () => void }> = ({ x, y, items, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Am rechten/unteren Rand nach innen klappen.
  const W = 220, H = items.length * 34 + 8;
  const left = Math.min(x, window.innerWidth - W - 8);
  const top = Math.min(y, window.innerHeight - H - 8);

  return (
    <>
      <div onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} style={{ position: 'fixed', inset: 0, zIndex: 70 }} />
      <div style={{ position: 'fixed', left, top, zIndex: 71, width: W, background: '#0c1120', border: '1px solid #1e2539', borderRadius: 8, padding: 4, boxShadow: '0 12px 34px rgba(0,0,0,.55)' }}>
        {items.map((it, i) => (
          <button key={i} onClick={() => { it.onClick(); onClose(); }} style={{
            display: 'block', width: '100%', textAlign: 'left', background: 'transparent',
            color: it.danger ? '#f87171' : '#cbd5e1', border: 'none', borderRadius: 6,
            padding: '8px 10px', fontSize: 12.5, cursor: 'pointer',
          }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#161d33')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >{it.label}</button>
        ))}
      </div>
    </>
  );
};

// ============================================================
// Alarm-Dialog (Block D) → POST /api/market/alerts
// ============================================================
export const AlarmDialog: React.FC<{ symbol: string; price: number; tickSize: number; onClose: () => void; onDone: (msg: string) => void }>
  = ({ symbol, price, tickSize, onClose, onDone }) => {
    const [val, setVal] = useState(price);
    const [dir, setDir] = useState<'auto' | 'above' | 'below'>('auto');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const submit = async () => {
      setBusy(true); setErr('');
      try {
        const body: any = { symbol, price: val, note: note || null };
        if (dir !== 'auto') body.direction = dir;
        const r = await fetch(`${API}/market/alerts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        if (!r.ok) throw new Error(await r.text());
        onDone(`Alarm gesetzt: ${symbol} @ ${fmtPrice(val, tickSize)}`);
        onClose();
      } catch (e: any) { setErr(e?.message || 'Fehler'); } finally { setBusy(false); }
    };

    return (
      <div style={overlayBg} onClick={onClose}>
        <div style={panel} onClick={(e) => e.stopPropagation()}>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: 15, marginBottom: 14 }}>🔔 Preis-Alarm — {symbol}</div>
          <label style={label}>Preis</label>
          <input style={input} type="number" step="any" value={val} onChange={(e) => setVal(+e.target.value)} autoFocus />
          <div style={{ height: 12 }} />
          <label style={label}>Richtung</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {(['auto', 'above', 'below'] as const).map(d => (
              <button key={d} onClick={() => setDir(d)} style={{
                ...btnGhost, flex: 1, padding: '7px 0',
                background: dir === d ? '#8b5cf6' : 'transparent', color: dir === d ? '#fff' : '#8b93a7',
                borderColor: dir === d ? '#8b5cf6' : '#1e2539',
              }}>{d === 'auto' ? 'Auto' : d === 'above' ? '⬆ Über' : '⬇ Unter'}</button>
            ))}
          </div>
          <div style={{ height: 12 }} />
          <label style={label}>Notiz (optional)</label>
          <input style={input} value={note} onChange={(e) => setNote(e.target.value)} placeholder="z. B. VAH-Break" />
          {err && <div style={{ color: '#f87171', fontSize: 12, marginTop: 10 }}>{err}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button style={btnGhost} onClick={onClose}>Abbrechen</button>
            <button style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={submit}>{busy ? '…' : 'Alarm setzen'}</button>
          </div>
        </div>
      </div>
    );
  };

// ============================================================
// Snapshot-Dialog (Block H) → POST /api/market/snapshot
// ============================================================
export const SnapshotDialog: React.FC<{ image: string; symbol: string; onClose: () => void; onDone: (msg: string) => void }>
  = ({ image, symbol, onClose, onDone }) => {
    const [target, setTarget] = useState<'trade' | 'journal'>('trade'); // Trade default
    const [trades, setTrades] = useState<Trade[]>([]);
    const [tradeId, setTradeId] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [title, setTitle] = useState(`${symbol} Chart`);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
      tradesApi.list().then(r => setTrades(r.data || [])).catch(() => setTrades([]));
    }, []);

    const filtered = trades.filter(t => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (t.asset || '').toLowerCase().includes(q) || (t.trade_date || '').includes(q) || (t.session || '').toLowerCase().includes(q);
    }).slice(0, 60);

    const submit = async () => {
      if (target === 'trade' && !tradeId) { setErr('Bitte einen Trade auswählen.'); return; }
      setBusy(true); setErr('');
      try {
        const body: any = { image, target, title, note: note || null };
        if (target === 'trade') body.trade_id = tradeId;
        const r = await fetch(`${API}/market/snapshot`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        if (!r.ok) throw new Error(await r.text());
        onDone(target === 'trade' ? 'Snapshot an Trade gehängt.' : 'Snapshot als Journal-Eintrag gespeichert.');
        onClose();
      } catch (e: any) { setErr(e?.message || 'Fehler'); } finally { setBusy(false); }
    };

    return (
      <div style={overlayBg} onClick={onClose}>
        <div style={{ ...panel, width: 540 }} onClick={(e) => e.stopPropagation()}>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: 15, marginBottom: 12 }}>📷 Snapshot speichern</div>
          <img src={image} alt="snapshot" style={{ width: '100%', borderRadius: 8, border: '1px solid #1e2539', marginBottom: 14, maxHeight: 220, objectFit: 'contain', background: '#0a0e1a' }} />

          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            {(['trade', 'journal'] as const).map(t => (
              <button key={t} onClick={() => setTarget(t)} style={{
                ...btnGhost, flex: 1,
                background: target === t ? '#8b5cf6' : 'transparent', color: target === t ? '#fff' : '#8b93a7',
                borderColor: target === t ? '#8b5cf6' : '#1e2539',
              }}>{t === 'trade' ? 'An Trade hängen' : 'Neuer Journal-Eintrag'}</button>
            ))}
          </div>

          {target === 'trade' && (
            <>
              <input style={{ ...input, marginBottom: 8 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Trade suchen (Asset / Datum / Session)…" />
              <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #1e2539', borderRadius: 8 }}>
                {filtered.length === 0 && <div style={{ color: '#5b6678', fontSize: 12, padding: 12 }}>Keine Trades gefunden.</div>}
                {filtered.map(t => (
                  <button key={t.id} onClick={() => setTradeId(t.id)} style={{
                    display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center',
                    background: tradeId === t.id ? '#161d33' : 'transparent', color: '#cbd5e1',
                    border: 'none', borderBottom: '1px solid #141a2c', padding: '8px 10px', fontSize: 12, cursor: 'pointer', textAlign: 'left',
                  }}>
                    <span><b style={{ color: '#fff' }}>{t.asset}</b> · {t.trade_date} · {t.session}</span>
                    <span style={{ color: t.pnl >= 0 ? '#22c55e' : '#ef4444', fontFamily: 'monospace' }}>{t.pnl >= 0 ? '+' : ''}{t.pnl}</span>
                  </button>
                ))}
              </div>
              <div style={{ height: 12 }} />
            </>
          )}

          <label style={label}>Titel</label>
          <input style={input} value={title} onChange={(e) => setTitle(e.target.value)} />
          <div style={{ height: 10 }} />
          <label style={label}>Notiz (optional)</label>
          <input style={input} value={note} onChange={(e) => setNote(e.target.value)} />

          {err && <div style={{ color: '#f87171', fontSize: 12, marginTop: 10 }}>{err}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button style={btnGhost} onClick={onClose}>Abbrechen</button>
            <button style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={submit}>{busy ? '…' : 'Speichern'}</button>
          </div>
        </div>
      </div>
    );
  };

// kleiner Helfer für flüchtige Bestätigungen
export const useFlash = (): [string | null, (m: string) => void] => {
  const [msg, setMsg] = useState<string | null>(null);
  const tRef = useRef<any>(null);
  const flash = (m: string) => {
    setMsg(m);
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(() => setMsg(null), 2600);
  };
  return [msg, flash];
};
